import React from 'react'
import PropTypes from 'prop-types'

import { normalizeArrowKeys } from '../../utils/events'
import Wrapper from './Wrapper'
import DropdownButton from './DropdownButton'
import DropdownMenu from './DropdownMenu'

const callAll = (...fns) => (...args) => fns.forEach(fn => fn && fn(...args))
const generateId = () => Math.random().toString(36).substr(2, 9)
const notDisabled = element => !element.disabled

class Dropdown extends React.Component {
  static propTypes = {
    button: PropTypes.node.isRequired,
    children: PropTypes.node,
    id: PropTypes.string,
    menuProps: PropTypes.shape({
      align: PropTypes.oneOf(['left', 'right']),
      offset: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ])
    })
  }

  state = { isOpen: false }
  id = this.props.id || generateId()
  wrapper = React.createRef()
  menu = React.createRef()

  componentWillUnmount() {
    clearTimeout(this.blurTimer)
  }

  toggleOpen = () => {
    this.state.isOpen ? this.close() : this.open()
  }

  open = () => {
    this.setState(() => ({ isOpen: true }), () => {
      this.menu.current.focus()
    })
  }

  close = () => {
    this.setState(() => ({ isOpen: false }))
  }

  handleBlur = event => {
    this.blurTimer = setTimeout(() => {
      if (!document.activeElement || !this.wrapper.current.contains(document.activeElement)) {
        this.close()
      }
    }, 30)
  }

  focusPrevItem() {
    const items = [...this.menu.current.children].filter(notDisabled)
    const activeIndex = items.indexOf(document.activeElement)
    let nextIndex = activeIndex - 1
    if (nextIndex < 0) nextIndex = items.length - 1
    items[nextIndex].focus()
  }

  focusNextItem() {
    const items = [...this.menu.current.children].filter(notDisabled)
    const activeIndex = items.indexOf(document.activeElement)
    let nextIndex = activeIndex + 1
    if (nextIndex >= items.length) nextIndex = 0
    items[nextIndex].focus()
  }

  focusFirstItem() {
    const items = [...this.menu.current.children].filter(notDisabled)
    items[0].focus()
  }

  focusEndItem() {
    const items = [...this.menu.current.children].filter(notDisabled)
    items[items.length - 1].focus()
  }

  keyHandlers = {
    ArrowUp(event) {
      event.preventDefault()
      if (this.state.isOpen) {
        this.focusPrevItem()
      } else {
        this.open()
      }
    },
    ArrowDown(event) {
      event.preventDefault()
      if (this.state.isOpen) {
        this.focusNextItem()
      } else {
        this.open()
      }
    },
    Home(event) {
      event.preventDefault()
      if (this.state.isOpen) {
        this.focusFirstItem()
      }
    },
    End(event) {
      event.preventDefault()
      if (this.state.isOpen) {
        this.focusEndItem()
      }
    },
    Escape() {
      if (this.state.isOpen) {
        this.close()
      }
    }
  }

  handleKeyDown = event => {
    const key = normalizeArrowKeys(event)
    if (key in this.keyHandlers) {
      this.keyHandlers[key].call(this, event)
    }
  }

  render() {
    const {
      isOpen
    } = this.state
    const {
      button,
      children,
      menuProps
    } = this.props

    return (
      <Wrapper
        ref={this.wrapper}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
      >
        <DropdownButton
          id={this.id}
          onClick={this.toggleOpen}
          aria-haspopup
          aria-expanded={isOpen}
        >
          {button}
        </DropdownButton>
        <DropdownMenu
          {...menuProps}
          aria-labelledby={this.id}
          role='menu'
          tabIndex={isOpen ? -1 : null}
          ref={this.menu}
        >
          {isOpen && React.Children.map(children, (child, index) => (
            React.cloneElement(child, {
              role: 'menuitem',
              onClick: callAll(child.props.onClick, this.close)
            })
          ))}
        </DropdownMenu>
      </Wrapper>
    )
  }
}

export default Dropdown
